import { Base, withMountWhenInView } from '@studiometa/js-toolkit';
import { clamp } from '@studiometa/js-toolkit/utils';

/**
 * Card component with 3D tilt, parallax, and hover effects.
 * @augments {Base<{
 *   $refs: { glow: HTMLElement, logo: HTMLElement[], background: HTMLElement[] },
 *   $options: { maxRotation: number, maxParallax: number, maxTranslate: number, liftZ: number }
 * }>}
 */
export default class Card extends withMountWhenInView(Base) {
  static config = {
    name: 'Card',
    emits: ['activated'],
    refs: ['glow', 'logo[]', 'background[]'],
    options: {
      maxRotation: { type: Number, default: 15 },
      maxParallax: { type: Number, default: 15 },
      maxTranslate: { type: Number, default: 20 },
      liftZ: { type: Number, default: 50 },
    },
  };

  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------

  isRevealed = false;
  isHovering = false;

  /** @type {((event: DeviceOrientationEvent) => void)|null} */
  #handleOrientationBound = null;

  /** @type {number|null} */
  #gyroAnimationFrame = null;

  /** Target values for gyroscope (before smoothing) */
  #gyroTarget = { x: 0, y: 0 };

  /** Current transform values */
  transform = {
    rotateX: 0,
    rotateY: 0,
    parallaxX: 0,
    parallaxY: 0,
    translateX: 0,
    translateY: 0,
    translateZ: 0,
  };

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   *
   */
  mounted() {
    this.#initStyles();
    this.#initGyroscope();
  }

  /**
   *
   */
  destroyed() {
    if (this.#handleOrientationBound) {
      window.removeEventListener('deviceorientation', this.#handleOrientationBound);
    }
    if (this.#gyroAnimationFrame) {
      cancelAnimationFrame(this.#gyroAnimationFrame);
    }
  }

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  /**
   *
   */
  onClick() {
    this.reveal();
  }

  /**
   *
   */
  onMouseenter() {
    if (this.isRevealed) return;

    this.isHovering = true;
    this.$el.classList.add('is-hovering');
    this.transform.translateZ = this.$options.liftZ;
    this.#applyTransform();
  }

  /**
   *
   */
  onMouseleave() {
    this.isHovering = false;
    this.$el.classList.remove('is-hovering');
    this.#resetTransform();
  }

  /** @param {{ x: number, y: number }} props */
  moved({ x, y }) {
    if (!this.isHovering) return;

    const rect = this.$el.getBoundingClientRect();
    const mouseX = x - rect.left;
    const mouseY = y - rect.top;

    this.#updateGlow(clamp(mouseX, 0, rect.width), clamp(mouseY, 0, rect.height));
    this.#calculateTransform(mouseX, mouseY, rect, x, y);
    this.#applyTransform();
  }

  // -------------------------------------------------------------------------
  // Public methods
  // -------------------------------------------------------------------------

  /**
   * Reveal the card (flip to front).
   */
  reveal() {
    if (this.isRevealed) return;

    this.isRevealed = true;
    this.isHovering = false;

    this.$el.classList.add('is-active');
    this.$el.classList.remove('is-hovering');
    this.$emit('activated', this);

    this.#resetTransform();
    this.#hideGlow();
  }

  // -------------------------------------------------------------------------
  // Gyroscope (mobile)
  // -------------------------------------------------------------------------

  /**
   *
   */
  #initGyroscope() {
    if (!window.DeviceOrientationEvent) return;

    this.#handleOrientationBound = this.#handleOrientation.bind(this);
    window.addEventListener('deviceorientation', this.#handleOrientationBound);
    this.#startGyroLoop();
  }

  /** @param {DeviceOrientationEvent} event */
  #handleOrientation(event) {
    if (this.isRevealed || this.isHovering) return;

    const { beta, gamma } = event;
    if (beta === null || gamma === null) return;

    // Normalize with wider range for more sensitivity (matching mouse feel)
    this.#gyroTarget.x = clamp(gamma / 20, -1, 1);
    this.#gyroTarget.y = clamp((beta - 45) / 20, -1, 1);
  }

  /**
   * Animation loop for smooth gyroscope interpolation
   */
  #startGyroLoop() {
    const smoothingFactor = 0.08;

    const loop = () => {
      if (this.isRevealed) return;
      if (this.isHovering) {
        this.#gyroAnimationFrame = requestAnimationFrame(loop);
        return;
      }

      const { maxRotation, maxParallax, liftZ } = this.$options;

      // Lerp current values towards target
      const currentX = this.transform.rotateY / -maxRotation || 0;
      const currentY = this.transform.rotateX / maxRotation || 0;

      const newX = currentX + (this.#gyroTarget.x - currentX) * smoothingFactor;
      const newY = currentY + (this.#gyroTarget.y - currentY) * smoothingFactor;

      this.transform.rotateX = newY * maxRotation;
      this.transform.rotateY = -newX * maxRotation;
      this.transform.parallaxX = newX * maxParallax;
      this.transform.parallaxY = newY * maxParallax;

      // Subtle lift based on tilt amount
      const tiltMagnitude = Math.sqrt(newX * newX + newY * newY);
      this.transform.translateZ = tiltMagnitude * liftZ * 0.5;

      this.#applyTransform();
      this.#gyroAnimationFrame = requestAnimationFrame(loop);
    };

    this.#gyroAnimationFrame = requestAnimationFrame(loop);
  }

  // -------------------------------------------------------------------------
  // Transform helpers
  // -------------------------------------------------------------------------

  /**
   *
   */
  #initStyles() {
    this.$el.style.setProperty('--tilt-x', '0deg');
    this.$el.style.setProperty('--tilt-y', '0deg');
    this.$el.style.setProperty('--translate-x', '0px');
    this.$el.style.setProperty('--translate-y', '0px');
    this.$el.style.setProperty('--translate-z', '0px');

    this.$refs.logo?.forEach((el) => {
      el.style.transform = 'translate(-50%, -50%)';
    });

    this.$refs.background?.forEach((el) => {
      el.style.transform = 'translate(0px, 0px)';
    });
  }

  /**
   * Calculate transform values based on mouse position.
   * @param {number} mouseX Mouse X relative to card
   * @param {number} mouseY Mouse Y relative to card
   * @param {DOMRect} rect Card bounding rect
   * @param {number} pageX Mouse X relative to page
   * @param {number} pageY Mouse Y relative to page
   */
  #calculateTransform(mouseX, mouseY, rect, pageX, pageY) {
    const normalizedX = (mouseX / rect.width - 0.5) * 2;
    const normalizedY = (mouseY / rect.height - 0.5) * 2;
    const { maxRotation, maxParallax, maxTranslate, liftZ } = this.$options;

    this.transform.rotateX = normalizedY * maxRotation;
    this.transform.rotateY = -normalizedX * maxRotation;

    this.transform.parallaxX = normalizedX * maxParallax;
    this.transform.parallaxY = normalizedY * maxParallax;

    const deck = this.$el.closest('.deck');
    if (deck) {
      const deckRect = deck.getBoundingClientRect();
      const deckCenterX = deckRect.left + deckRect.width / 2;
      const deckCenterY = deckRect.top + deckRect.height / 2;

      this.transform.translateX = clamp((pageX - deckCenterX) * 0.15, -maxTranslate, maxTranslate);
      this.transform.translateY = clamp((pageY - deckCenterY) * 0.15, -maxTranslate, maxTranslate);
    }

    this.transform.translateZ = liftZ;
  }

  /**
   *
   */
  #applyTransform() {
    const { rotateX, rotateY, translateX, translateY, translateZ, parallaxX, parallaxY } =
      this.transform;

    this.$el.style.setProperty('--tilt-x', `${rotateX}deg`);
    this.$el.style.setProperty('--tilt-y', `${rotateY}deg`);
    this.$el.style.setProperty('--translate-x', `${translateX}px`);
    this.$el.style.setProperty('--translate-y', `${translateY}px`);
    this.$el.style.setProperty('--translate-z', `${translateZ}px`);

    this.$refs.logo?.forEach((el) => {
      el.style.transform = `translate(calc(-50% + ${parallaxX * 0.5}px), calc(-50% + ${parallaxY * 0.5}px))`;
    });

    this.$refs.background?.forEach((el) => {
      el.style.transform = `translate(${parallaxX * 1.2}px, ${parallaxY * 1.2}px)`;
    });
  }

  /**
   *
   */
  #resetTransform() {
    this.transform = {
      rotateX: 0,
      rotateY: 0,
      parallaxX: 0,
      parallaxY: 0,
      translateX: 0,
      translateY: 0,
      translateZ: 0,
    };
    this.#applyTransform();
  }

  // -------------------------------------------------------------------------
  // Glow helpers
  // -------------------------------------------------------------------------

  /**
   * @param {number} x
   * @param {number} y
   */
  #updateGlow(x, y) {
    if (!this.$refs.glow || this.isRevealed) return;
    this.$refs.glow.style.left = `${x}px`;
    this.$refs.glow.style.top = `${y}px`;
  }

  /**
   *
   */
  #hideGlow() {
    if (this.$refs.glow) {
      this.$refs.glow.style.opacity = '0';
    }
  }
}
